import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import { Wallet, Loader2, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, PackageCheck, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAdminOutputFees, useCreateOutputFee, useUpdateOutputFee, useDeleteOutputFee, type ConferenceOutputFee } from "@/services/adminConferenceOutputService";
import { StatCard } from "@/pages/private/admin/conference-output/OutputDecisionsPage";

const emptyForm: Partial<ConferenceOutputFee> = {
    conference_id: null, publisher: "KNE", price: "", currency: "IDR", service_id: null,
    start_date: null, end_date: null, is_active: true,
};

export default function OutputFeesPage() {
    const { data: fees, isLoading } = useAdminOutputFees();
    const { data: conferences } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/conference-billing/accessible-conferences");
            return data.data;
        },
    });
    const createFee = useCreateOutputFee();
    const updateFee = useUpdateOutputFee();
    const deleteFee = useDeleteOutputFee();

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const stats = useMemo(() => {
        const list = fees || [];
        return { total: list.length, active: list.filter((f) => f.is_active).length, inactive: list.filter((f) => !f.is_active).length };
    }, [fees]);
    const totalItems = fees?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const currentFees = (fees ?? []).slice(startIndex, endIndex);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<any>(emptyForm);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    const openCreate = () => { setEditingId(null); setForm(emptyForm); setIsModalOpen(true); };
    const openEdit = (fee: ConferenceOutputFee) => {
        setEditingId(fee.id);
        setForm({
            ...fee,
            conference_id: fee.conference_id ? String(fee.conference_id) : null,
            start_date: fee.start_date ? fee.start_date.slice(0, 16) : "",
            end_date: fee.end_date ? fee.end_date.slice(0, 16) : "",
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const payload = {
            ...form,
            conference_id: form.conference_id ? parseInt(form.conference_id) : null,
            price: form.price ? parseFloat(form.price) : 0,
            service_id: form.service_id ? parseInt(form.service_id) : null,
            start_date: form.start_date || null,
            end_date: form.end_date || null,
        };
        try {
            if (editingId) await updateFee.mutateAsync({ id: editingId, payload });
            else await createFee.mutateAsync(payload);
            toast.success(editingId ? "Biaya berhasil diperbarui" : "Biaya berhasil ditambahkan");
            setIsModalOpen(false);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Terjadi kesalahan saat menyimpan data.");
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirmId) return;
        try {
            await deleteFee.mutateAsync(deleteConfirmId);
            toast.success("Biaya berhasil dihapus");
            setDeleteConfirmId(null);
        } catch {
            toast.error("Gagal menghapus data");
        }
    };

    const formatCurrency = (amount: string | number, currency: string) =>
        new Intl.NumberFormat(currency === "USD" ? "en-US" : "id-ID", { style: "currency", currency, minimumFractionDigits: 0 }).format(Number(amount));

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none"><Wallet className="w-64 h-64 text-blue-600" /></div>
                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 flex-shrink-0 border border-blue-100 dark:border-blue-800/50 shadow-sm"><Wallet className="w-8 h-8" /></div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Biaya Publikasi Proceeding</h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">Atur tarif biaya publikasi prosiding per publisher / track. Kosongkan track untuk berlaku ke semua.</p>
                    </div>
                </div>
                <Button onClick={openCreate} className="h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/20 relative z-10">
                    <Plus className="w-5 h-5 mr-2" /> Tambah Biaya
                </Button>
            </div>

            {/* Kartu Total */}
            <div className="grid grid-cols-3 gap-4">
                <StatCard icon={Wallet} label="Total Pengaturan Biaya" value={stats.total} color="blue" />
                <StatCard icon={PackageCheck} label="Aktif" value={stats.active} color="emerald" />
                <StatCard icon={Ban} label="Nonaktif" value={stats.inactive} color="slate" />
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[700px]">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-6 py-4 font-semibold">Publisher</th>
                                <th className="px-6 py-4 font-semibold">Track</th>
                                <th className="px-6 py-4 font-semibold text-center">Harga</th>
                                <th className="px-6 py-4 font-semibold text-center">Service ID</th>
                                <th className="px-6 py-4 font-semibold text-center">Status</th>
                                <th className="px-6 py-4 font-semibold text-right w-24 sticky right-0 bg-slate-50/80 dark:bg-slate-800/50 backdrop-blur-sm z-10 border-l border-slate-200 dark:border-slate-800 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {isLoading ? (
                                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />Memuat data...</td></tr>
                            ) : totalItems === 0 ? (
                                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">Belum ada pengaturan biaya.</td></tr>
                            ) : (
                                currentFees.map((fee) => {
                                    const conf = conferences?.find((c: any) => c.conference_id === fee.conference_id);
                                    return (
                                        <tr key={fee.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                            <td className="px-6 py-4 font-bold text-slate-900 dark:text-white">{fee.publisher}</td>
                                            <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{conf?.name || "Semua Track"}</td>
                                            <td className="px-6 py-4 text-center font-mono text-sm">{formatCurrency(fee.price, fee.currency)}</td>
                                            <td className="px-6 py-4 text-center text-sm text-slate-500">{fee.service_id ?? "-"}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-md ${fee.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>
                                                    {fee.is_active ? "Aktif" : "Nonaktif"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 border-l border-slate-100 dark:border-slate-800/60 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">
                                                <div className="flex items-center justify-end gap-1">
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button variant="ghost" size="icon" onClick={() => openEdit(fee)} className="h-8 w-8 bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400"><Edit2 className="w-4 h-4" /></Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>Edit Biaya</p></TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmId(fee.id)} className="h-8 w-8 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>Hapus Biaya</p></TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {totalItems > 0 && !isLoading && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <span className="text-slate-500 font-medium text-xs sm:text-sm">
                            Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                        </span>
                        <div className="flex items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="h-8 px-3 rounded-lg"><ChevronLeft className="w-4 h-4" /></Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium min-w-[3rem] text-center">{page} / {totalPages}</span>
                                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 px-3 rounded-lg"><ChevronRight className="w-4 h-4" /></Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-white dark:bg-slate-900">
                    <DialogHeader className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                        <DialogTitle>{editingId ? "Edit Biaya" : "Tambah Biaya"}</DialogTitle>
                        <DialogDescription>Atur tarif publikasi prosiding beserta layanan pembayaran (Smart Payment).</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
                        <div className="space-y-2">
                            <Label>Publisher <span className="text-red-500">*</span></Label>
                            <Input value={form.publisher ?? ""} onChange={(e) => setForm({ ...form, publisher: e.target.value })} placeholder="KNE, SHS, dst" required />
                        </div>
                        <div className="space-y-2">
                            <Label>Track Konferensi <span className="text-slate-400 text-xs font-normal">(Opsional - kosongkan untuk semua track)</span></Label>
                            <Select value={form.conference_id ?? "all"} onValueChange={(v) => setForm({ ...form, conference_id: v === "all" ? null : v })}>
                                <SelectTrigger className="h-10"><SelectValue placeholder="Semua Track" /></SelectTrigger>
                                <SelectContent className="max-h-[250px]">
                                    <SelectItem value="all">Semua Track</SelectItem>
                                    {conferences?.map((c: any) => <SelectItem key={c.conference_id} value={String(c.conference_id)}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Harga <span className="text-red-500">*</span></Label>
                                <Input type="number" step="0.01" value={form.price ?? ""} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Mata Uang</Label>
                                <Select value={form.currency ?? "IDR"} onValueChange={(v) => setForm({ ...form, currency: v })}>
                                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="IDR">IDR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Service ID (Smart Payment) <span className="text-red-500">*</span></Label>
                            <Input type="number" value={form.service_id ?? ""} onChange={(e) => setForm({ ...form, service_id: e.target.value })} required />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Berlaku Mulai</Label>
                                <Input type="datetime-local" value={form.start_date ?? ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Berlaku Sampai</Label>
                                <Input type="datetime-local" value={form.end_date ?? ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                            </div>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                            <Label>Aktif</Label>
                            <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                        </div>
                        <DialogFooter className="pt-4 mt-2 border-t border-slate-200 dark:border-slate-800">
                            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">Batal</Button>
                            <Button type="submit" disabled={createFee.isPending || updateFee.isPending} className="rounded-xl bg-blue-600 hover:bg-blue-700">
                                {(createFee.isPending || updateFee.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Simpan
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Hapus Biaya?</DialogTitle>
                        <DialogDescription>Tindakan ini tidak dapat dibatalkan.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Batal</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={deleteFee.isPending}>
                            {deleteFee.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Ya, Hapus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
